// Decides which synth patch plays each MIDI track. MidiMate names tracks after the sound it
// wants ("Rolling Bass", "Chord Stabs", "Rhodes", see SOUND_PALETTE in the generate-midi
// prompt), so the name is the primary signal; the General MIDI program and the note range
// are fallbacks for uploaded files.

export const STYLES = ['house', 'lofi', 'trap', 'futurebass', 'pop', 'acoustic'];

const ROLE_RULES = [
  ['chords', /chord|stab|keys|rhodes|piano|organ|wurli|\bep\b|comp/],
  ['bass', /bass|808|\bsub\b|reese/],
  ['pad', /pad|string|atmos|drone|choir|ambien/],
  ['lead', /lead|hook|melod|bell|marimba|pluck|arp|kalimba|vocal|topline|riff|solo|flute|sax|trumpet|brass|horn|guitar|violin/],
];

export function roleOf(track) {
  if (track.channel === 9 || track.isDrum) return 'drums';
  const n = track.name.toLowerCase();
  for (const [role, re] of ROLE_RULES) if (re.test(n)) return role;
  const p = track.program;
  if (p >= 32 && p <= 39) return 'bass';
  if ((p >= 40 && p <= 55) || (p >= 88 && p <= 103)) return 'pad';
  if (p >= 56 && p <= 87) return 'lead';
  const pitches = track.notes.map((n) => n.midi).sort((a, b) => a - b);
  if (pitches.length && pitches[Math.floor(pitches.length / 2)] < 48) return 'bass';
  const starts = track.notes.map((n) => n.time);
  let together = 0;
  for (let i = 1; i < starts.length; i++) if (starts[i] - starts[i - 1] < 0.03) together++;
  return together > starts.length * 0.4 ? 'chords' : 'lead';
}

// Named sounds the model can ask for, matched against the track name
const NAME_PATCHES = [
  [/808|\bsub\b/, 'sub808'],
  [/reese|growl/, 'reese'],
  [/rolling|saw|acid|synth bass|plucky bass/, 'rolling_bass'],
  [/warm|finger|upright|acoustic bass|round|deep bass|\bsine/, 'warm_bass'],
  [/supersaw|super saw|trance|anthem|big chords|wide chords|future/, 'supersaw'],
  [/stab/, 'stab'],
  [/rhodes|wurli|electric piano|\bep\b|keys/, 'ep'],
  [/piano/, 'piano'],
  [/organ/, 'organ'],
  [/string|orchestra|violin|cello/, 'strings'],
  [/choir|vocal|vox/, 'pad'],
  [/pad|atmos|drone|ambien|texture/, 'pad'],
  [/bell|glock|celest|music box|chime/, 'bell'],
  [/marimba|mallet|kalimba|xylo|vibra|steel/, 'mallet'],
  [/pluck|arp|hook|riff|guitar|harp/, 'pluck'],
  [/brass|horn|trumpet|sax|trombone/, 'brass'],
  [/soft|mellow|flute|whistle|smooth|warm lead|sine lead/, 'soft_lead'],
  [/lead|melod|topline|solo|synth/, 'synth_lead'],
];

const PROGRAM_PATCHES = [
  [0, 7, 'piano'], [8, 15, 'bell'], [16, 23, 'organ'], [24, 31, 'pluck'],
  [32, 37, 'warm_bass'], [38, 39, 'rolling_bass'], [40, 55, 'strings'], [56, 63, 'brass'],
  [64, 79, 'soft_lead'], [80, 80, 'pluck'], [81, 87, 'synth_lead'], [88, 95, 'pad'], [96, 127, 'pad'],
];

const defaultBass = (style) =>
  style === 'trap' ? 'sub808' : ['lofi', 'acoustic', 'pop'].includes(style) ? 'warm_bass' : 'rolling_bass';

export function patchFor(track, role, style) {
  if (role === 'drums') return 'drums';
  const n = track.name.toLowerCase();
  for (const [re, patch] of NAME_PATCHES) {
    if (re.test(n)) {
      // Keep the name's intent but respect the role: "Supersaw Bass" is still a bass
      if (role === 'bass' && !['sub808', 'reese', 'rolling_bass', 'warm_bass'].includes(patch)) {
        return defaultBass(style);
      }
      return patch;
    }
  }
  for (const [lo, hi, patch] of PROGRAM_PATCHES) {
    if (track.program >= lo && track.program <= hi && track.name === '') return patch;
  }
  if (role === 'bass') return defaultBass(style);
  if (role === 'pad') return 'pad';
  if (role === 'chords') return style === 'house' ? 'stab' : style === 'futurebass' ? 'supersaw' : style === 'pop' ? 'piano' : 'ep';
  return style === 'futurebass' ? 'synth_lead' : style === 'lofi' ? 'soft_lead' : 'pluck';
}

// The overall feel decides the drum kit, sidechain and master treatment
export function styleOf(tracks, bpm) {
  const names = tracks.map((t) => t.name.toLowerCase()).join(' ');
  if (/trap|808|phonk|drill|hip.?hop|rap/.test(names)) return 'trap';
  if (/lo.?fi|dusty|boom.?bap|chill|jazz|neo.?soul|mellow/.test(names)) return 'lofi';
  if (/future bass|future|melodic dub|chillstep|kawaii/.test(names)) return 'futurebass';
  if (/house|techno|trance|disco|edm|dance|club|rave|garage|dnb|drum and bass|breaks/.test(names)) return 'house';
  if (/\bpop|indie|synthwave|r&b|rnb|ballad|piano/.test(names)) return 'pop';
  if (/rock|metal|punk|funk|soul|acoustic|folk|country|blues|orchestr|cinematic|bossa|latin/.test(names)) return 'acoustic';
  if (bpm >= 128 && bpm <= 160 && /pad|bell/.test(names) && !/kick|house/.test(names)) return 'trap';
  if (bpm < 95) return 'lofi';
  return 'house';
}

// Character words in a track name ("Bright Wide Supersaw Chords") become tone tweaks
export function modsOf(name) {
  const n = name.toLowerCase();
  return {
    bright: /\bbright|crisp|sparkl/.test(n), dark: /\bdark|dull|muffled/.test(n),
    wide: /\bwide|huge|massive/.test(n), narrow: /\bnarrow|mono|tight/.test(n),
    wet: /\bwet|washed|spacey|ambient|reverb/.test(n), dry: /\bdry|close|intimate/.test(n),
    soft: /\bsoft|gentle|smooth/.test(n), hard: /\bhard|punchy|aggressive|sharp/.test(n),
  };
}

export function describe(tracks, bpm) {
  const style = styleOf(tracks, bpm);
  return tracks.map((t) => {
    const role = roleOf(t);
    return { track: t, role, patch: patchFor(t, role, style), style, mods: modsOf(t.name) };
  });
}
